import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockResetIcon from '@mui/icons-material/LockReset'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { EditUserDialogProvider, useEditUserDialog } from './context/EditUserDialogContext'
import AlertDialog from 'components/AlertDialog'
import ConfirmDialog from 'components/ConfirmDialog'
import UserInformationForm from './components/UserInformationForm'
import UserPlansForm from './components/UserPlansForm'
import UserRolesForm from './components/UserRolesForm'
import UserSessionsView from './components/UserSessionsView'
import { useGlobalState } from 'contexts/GlobalStateContext'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `user-tab-${index}`,
    'aria-controls': `user-tabpanel-${index}`,
  }
}

interface EditUserDialogProps {
  open: boolean
  onClose: () => void
  userId?: string | null
  selectedOrganizationId?: string | null
  createMode?: boolean
}

const EditUserDialogContent: React.FC<{
  open: boolean
  onClose: () => void
  selectedOrganizationId?: string | null
  createMode: boolean
}> = ({ open, onClose, selectedOrganizationId, createMode }) => {
  const {
    activeTab,
    formDataChanged,
    formData,
    rootOrganizationId,
    user,
    isLoadingUser,
    refetchUserProfile,
    isLoadingRoles,
    isRolesError,
    profile,
    isLoadingProfile,
    isLoadingLoginHistory,
    isLoginHistoryError,
    requireUserResetPassword,
    loginHistory,
    sendUserInvite,
    sendUserChangePassword,
    isCreating,
    isUpdating,
    updateUserError,
    isDeletingUser,
    setActiveTab,
    handleSave,
    handleDeleteUser,
    validateForm,
  } = useEditUserDialog()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const { checkSubscriptionTierFeature } = useGlobalState()

  useEffect(() => {
    if (!updateUserError) return
    console.error('Error updating the user record:', updateUserError)
    if (updateUserError) {
      setErrorMessage('Error updating user record.')
      setShowErrorDialog(true)
    }
  }, [updateUserError])

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDeleteClick = () => setShowDeleteConfirmDialog(true)

  const handleDeleteConfirm = () => {
    setShowDeleteConfirmDialog(false)
    handleDeleteUser(formData.id, rootOrganizationId!)
    handleMenuClose()
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirmDialog(false)
    handleMenuClose()
  }

  const handlePasswordResetClick = () => {
    if (!formData.id) {
      console.error('User ID is missing')
      return
    }
    console.log('Require Password Reset clicked for user:', formData.id)
    requireUserResetPassword(formData.id)
    handleMenuClose()
  }

  const handleSendInviteClick = () => {
    if (!formData.id) {
      console.error('User ID is missing')
      return
    }
    console.log('Send Invite clicked for user:', formData.id)
    sendUserInvite(formData.id)
    handleMenuClose()
  }

  const handleSendChangePasswordClick = () => {
    if (!formData.id) {
      console.error('User ID is missing')
      return
    }
    console.log('Send Password Change clicked for user:', formData.id)
    sendUserChangePassword(formData.id)
    handleMenuClose()
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false)
    setErrorMessage('')
    onClose()
  }

  if (isLoadingRoles || isLoadingProfile || isLoadingLoginHistory) return null

  if (!createMode && (isLoadingUser || !user)) return null

  if (isRolesError) {
    console.error('Error Loading Roles')
    return null
  }

  if (isLoginHistoryError) {
    console.error('Error Loading Login History')
    return null
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 24,
          },
          style: {
            minHeight: '600px', // Set minimum height to 50% of viewport height
            maxHeight: '600px',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography variant="h5" component="div">
            {createMode ? 'Creating User' : 'Editing User'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            disabled={isCreating || isUpdating || isDeletingUser}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="user tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Information" {...a11yProps(0)} />
              <Tab label="Roles" {...a11yProps(1)} />
              {!createMode && checkSubscriptionTierFeature('INCENTIVE.ALL') && <Tab label="Plans" {...a11yProps(2)} />}
              {!createMode && <Tab label="Sessions" {...a11yProps(3)} />}
            </Tabs>
            {!createMode && (
              <>
                <Tooltip title="More actions">
                  <IconButton
                    color="primary"
                    onClick={handleMenuClick}
                    disabled={isCreating || isUpdating || isDeletingUser}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <List sx={{ p: 0 }}>
                    <ListItemButton
                      onClick={handlePasswordResetClick}
                      disabled={isCreating || isUpdating || isDeletingUser}
                      sx={{ alignItems: 'center', py: 1 }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <LockResetIcon sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Require Password Change"
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            lineHeight: 1.43,
                          },
                        }}
                      />
                    </ListItemButton>
                    {loginHistory.length <= 0 ? (
                      <ListItemButton
                        onClick={handleSendInviteClick}
                        disabled={isCreating || isUpdating || isDeletingUser}
                        sx={{ alignItems: 'center', py: 1 }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MailOutlineIcon sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={user?.user.inviteSent ? 'Resend Invite' : 'Send Invite'}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              lineHeight: 1.43,
                            },
                          }}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        onClick={handleSendChangePasswordClick}
                        disabled={isCreating || isUpdating || isDeletingUser}
                        sx={{ alignItems: 'center', py: 1 }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MailOutlineIcon sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={'Send Password Change'}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              lineHeight: 1.43,
                            },
                          }}
                        />
                      </ListItemButton>
                    )}
                    <ListItemButton
                      onClick={handleDeleteClick}
                      disabled={isDeletingUser}
                      sx={{ alignItems: 'center', py: 1 }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DeleteOutlineIcon sx={{ color: 'error.main', fontSize: '1.25rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Delete User"
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            lineHeight: 1.43,
                          },
                        }}
                      />
                    </ListItemButton>
                  </List>
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        <DialogContent dividers>
          <TabPanel value={activeTab} index={0}>
            <UserInformationForm defaultOrganizationId={selectedOrganizationId} createMode={createMode} />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <UserRolesForm />
          </TabPanel>
          {checkSubscriptionTierFeature('INCENTIVE.ALL') && (
            <TabPanel value={activeTab} index={2}>
              <UserPlansForm
                profile={profile}
                user={user}
                isLoadingUser={isLoadingUser}
                refetchUser={refetchUserProfile}
              />
            </TabPanel>
          )}
          <TabPanel value={activeTab} index={3}>
            <UserSessionsView />
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={onClose} variant="outlined" color="warning" disabled={isDeletingUser}>
            Cancel
          </Button>
          <Button
            disabled={!validateForm() || isCreating || isUpdating || isDeletingUser || !formDataChanged}
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            {isCreating || isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        show={showErrorDialog}
        onClose={handleCloseErrorDialog}
        title="Error"
        message={errorMessage}
        okText="OK"
        okColor="error"
      />

      <ConfirmDialog
        show={showDeleteConfirmDialog}
        onCancel={handleDeleteCancel}
        title="Confirm Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  createMode = false,
  onClose,
  userId = null,
  selectedOrganizationId = null,
}) => {
  return (
    <EditUserDialogProvider open={open} createMode={createMode} onClose={onClose} userId={userId}>
      <EditUserDialogContent
        open={open}
        onClose={onClose}
        selectedOrganizationId={selectedOrganizationId}
        createMode={createMode}
      />
    </EditUserDialogProvider>
  )
}

export default EditUserDialog
